import type { Controls } from '../core/device';
import { clamp } from '../core/math';
import type { Random } from '../core/Random';
import { banner, roundRect } from './draw';
import type { MiniGame, MiniInput } from './types';

export type HoleContent =
  { kind: 'fish'; lbs: number } | { kind: 'snapper' } | { kind: 'mud' } | { kind: 'boot' };

export type NoodleOutcome =
  | { kind: 'caught'; lbs: number }
  | { kind: 'snapper' }
  | { kind: 'mud' }
  | { kind: 'boot' }
  | { kind: 'ripped'; lbs: number }
  | { kind: 'escaped'; lbs: number };

const HOLE_X = [70, 180, 290];
const HOLE_Y = 158;
const REACH_TIME = 1.3;
const FIGHT_LIMIT = 24;

/** Rolls what's lurking in a catfish hole. Mostly catfish, mostly small. */
export function rollHole(rng: Random): HoleContent {
  const r = rng.next();
  if (r < 0.68) return { kind: 'fish', lbs: Math.round(4 + rng.next() ** 2.2 * 42) };
  if (r < 0.82) return { kind: 'snapper' };
  if (r < 0.94) return { kind: 'mud' };
  return { kind: 'boot' };
}

/**
 * Noodling: stick your arm in a hole in the crick bank and let a catfish bite it, then
 * wrestle it out. Hold to pull; ease off when the line's about to snap (your arm).
 */
export class NoodlingGame implements MiniGame {
  readonly title = "Catfish Noodlin'";
  readonly width = 360;
  readonly height = 240;
  phase: 'pick' | 'reach' | 'fight' | 'over' = 'pick';
  readonly holes: HoleContent[];
  cursor = 0;
  picked = -1;
  /** 0..1: how hard you're pulling. Keep it in the zone. */
  tension = 0.42;
  /** 0..1: the fish's fight. Drains while you're in the zone. */
  fight = 1;
  outcome: NoodleOutcome | null = null;
  private t = 0;
  private autoCycle = true;
  private low = 0;
  private thrashIn = 0.8;
  private thrash = 0;
  private lbs = 0;

  constructor(
    private readonly rng: Random,
    /** 0..1 how drunk you are: the zone wanders. */
    private readonly drunk = 0,
    holes?: HoleContent[],
  ) {
    this.holes = holes ?? [rollHole(rng), rollHole(rng), rollHole(rng)];
  }

  get done(): boolean {
    return this.phase === 'over' && this.t > 1.1;
  }

  /** Bigger fish fight harder: a tighter zone and slower to tire. */
  private get size(): number {
    return clamp((this.lbs - 4) / 38, 0, 1);
  }

  zone(time = this.t): [number, number] {
    const s = this.size;
    const wander = Math.sin(time * 1.7) * 0.07 * this.drunk;
    return [0.33 + s * 0.05 + wander, 0.75 - s * 0.12 + wander];
  }

  hint(controls: Controls): string {
    if (this.phase === 'pick') {
      const how = { keys: '←/→ then Space, or click', touch: 'tap it', pad: '←/→ then A' }[controls];
      return `Pick a hole (${how}). Pray it's a catfish.`;
    }
    const hold = { keys: 'Hold Space/E', touch: 'Hold', pad: 'Hold A' }[controls];
    return `${hold} to pull. Ease off before your arm gives out. Keep it in the green.`;
  }

  update(dt: number, input: MiniInput): void {
    this.t += dt;
    switch (this.phase) {
      case 'pick':
        return this.updatePick(input);
      case 'reach':
        if (this.t >= REACH_TIME) this.reveal();
        return;
      case 'fight':
        return this.updateFight(dt, input);
      default:
        return;
    }
  }

  private updatePick(input: MiniInput): void {
    if (input.keys.includes('ArrowLeft')) {
      this.autoCycle = false;
      this.cursor = (this.cursor + 2) % 3;
    }
    if (input.keys.includes('ArrowRight')) {
      this.autoCycle = false;
      this.cursor = (this.cursor + 1) % 3;
    }
    for (const [i, k] of ['Digit1', 'Digit2', 'Digit3'].entries())
      if (input.keys.includes(k)) return this.pick(i);
    if (this.autoCycle) this.cursor = Math.floor(this.t / 0.75) % 3;
    if (input.tap) {
      const i = HOLE_X.findIndex((x) => Math.abs(input.tap!.x - x) < 50);
      if (i >= 0) return this.pick(i);
    }
    if (input.pressed && !input.tap) this.pick(this.cursor);
  }

  /** 0..1 how hard the fish is thrashing right now. */
  get thrashing(): number {
    return this.thrash;
  }

  /** How big the fish on the line is (0 until one bites). */
  get fishLbs(): number {
    return this.lbs;
  }

  pick(i: number): void {
    this.picked = i;
    this.phase = 'reach';
    this.t = 0;
  }

  private reveal(): void {
    const hole = this.holes[this.picked] ?? { kind: 'mud' };
    this.t = 0;
    if (hole.kind === 'fish') {
      this.lbs = hole.lbs;
      this.phase = 'fight';
      return;
    }
    this.finish(hole);
  }

  private updateFight(dt: number, input: MiniInput): void {
    this.tension += (input.held ? 0.72 : -0.56) * dt;
    this.thrashIn -= dt;
    if (this.thrashIn <= 0) {
      this.thrashIn = this.rng.range(0.45, 1.3);
      const kick = (0.08 + this.size * 0.22) * this.rng.range(0.6, 1);
      this.tension += this.rng.chance(0.7) ? kick : -kick;
      this.thrash = 1;
    }
    this.thrash = Math.max(0, this.thrash - dt * 3);
    this.tension = clamp(this.tension, 0, 1);
    const [lo, hi] = this.zone();
    if (this.tension >= lo && this.tension <= hi) this.fight -= dt * (0.32 - this.size * 0.2);
    this.low = this.tension < 0.06 ? this.low + dt : 0;
    if (this.fight <= 0) this.finish({ kind: 'caught', lbs: this.lbs });
    else if (this.tension >= 1) this.finish({ kind: 'ripped', lbs: this.lbs });
    else if (this.low > 1.4 || this.t > FIGHT_LIMIT) this.finish({ kind: 'escaped', lbs: this.lbs });
  }

  private finish(outcome: HoleContent | NoodleOutcome): void {
    this.outcome = outcome.kind === 'fish' ? { kind: 'caught', lbs: outcome.lbs } : outcome;
    this.phase = 'over';
    this.t = 0;
  }

  // --- drawing ----------------------------------------------------------------------------

  draw(g: CanvasRenderingContext2D, time: number): void {
    const W = this.width;
    const H = this.height;
    const water = g.createLinearGradient(0, 0, 0, H);
    water.addColorStop(0, '#3b4a2a');
    water.addColorStop(0.55, '#2f3a22');
    water.addColorStop(1, '#4a3a28');
    g.fillStyle = water;
    g.fillRect(0, 0, W, H);
    // Ripples and the muddy bank.
    g.strokeStyle = 'rgba(200,220,170,0.15)';
    g.lineWidth = 1.5;
    for (let i = 0; i < 6; i++) {
      const y = 22 + i * 16 + Math.sin(time * 1.3 + i) * 2;
      g.beginPath();
      g.moveTo(10 + ((i * 53) % 90), y);
      g.quadraticCurveTo(W / 2, y + Math.sin(time + i) * 4, W - 10 - ((i * 37) % 80), y);
      g.stroke();
    }
    g.fillStyle = '#5a4630';
    g.beginPath();
    g.moveTo(0, 125);
    for (let x = 0; x <= W; x += 20) g.lineTo(x, 125 + Math.sin(x * 0.07) * 6);
    g.lineTo(W, H);
    g.lineTo(0, H);
    g.fill();

    if (this.phase === 'pick' || this.phase === 'reach') this.drawHoles(g, time);
    if (this.phase === 'reach') this.drawArm(g, clamp(this.t / REACH_TIME, 0, 1), time);
    if (this.phase === 'fight') this.drawFight(g, time);
    if (this.phase === 'over') this.drawOver(g);
  }

  private drawHoles(g: CanvasRenderingContext2D, time: number): void {
    HOLE_X.forEach((x, i) => {
      g.fillStyle = '#1b140e';
      g.beginPath();
      g.ellipse(x, HOLE_Y, 34, 20, 0, 0, Math.PI * 2);
      g.fill();
      const active = this.phase === 'pick' ? i === this.cursor : i === this.picked;
      if (active) {
        g.strokeStyle = '#ffd400';
        g.lineWidth = 3;
        g.beginPath();
        g.ellipse(x, HOLE_Y, 40 + Math.sin(time * 6) * 2, 25, 0, 0, Math.PI * 2);
        g.stroke();
      }
      g.fillStyle = 'rgba(255,255,255,0.75)';
      g.font = '16px Anton, Impact, sans-serif';
      g.textAlign = 'center';
      g.fillText(String(i + 1), x, HOLE_Y + 44);
    });
  }

  private drawArm(g: CanvasRenderingContext2D, k: number, time: number): void {
    const x = HOLE_X[this.picked] ?? 180;
    g.fillStyle = '#e0a87e';
    g.strokeStyle = '#8a5a3a';
    g.lineWidth = 2;
    roundRect(g, x - 14, HOLE_Y - 6 + (1 - k) * 80, 28, 140, 12);
    g.fill();
    g.stroke();
    for (let i = 0; i < 4; i++) {
      g.fillStyle = 'rgba(220,240,200,0.55)';
      g.beginPath();
      g.arc(x - 20 + i * 13, HOLE_Y - 18 - ((time * 40 + i * 17) % 50), 3, 0, Math.PI * 2);
      g.fill();
    }
  }

  private drawFight(g: CanvasRenderingContext2D, time: number): void {
    // The catfish, thrashing on your arm.
    const cx = 160;
    const cy = 112;
    const s = 0.7 + this.size * 0.6;
    g.save();
    g.translate(cx, cy);
    g.rotate(Math.sin(time * 14) * 0.18 * (0.3 + this.thrash));
    g.scale(s, s);
    g.fillStyle = '#4b5a4a';
    g.beginPath();
    g.ellipse(0, 0, 70, 26, 0, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.moveTo(62, 0);
    g.lineTo(98, -22 + Math.sin(time * 18) * 6);
    g.lineTo(98, 22 + Math.sin(time * 18) * 6);
    g.fill();
    g.fillStyle = '#c9c2a0';
    g.beginPath();
    g.ellipse(-8, 10, 50, 12, 0, 0, Math.PI);
    g.fill();
    g.strokeStyle = '#2b3329';
    g.lineWidth = 2;
    for (const d of [-1, 1]) {
      g.beginPath();
      g.moveTo(-66, 4 * d);
      g.quadraticCurveTo(-92, 14 * d + Math.sin(time * 9) * 5, -104, 30 * d);
      g.stroke();
    }
    g.fillStyle = '#111';
    g.beginPath();
    g.arc(-48, -8, 4, 0, Math.PI * 2);
    g.fill();
    // Your arm, in its mouth.
    g.fillStyle = '#e0a87e';
    g.fillRect(-120, -7, 60, 14);
    g.restore();

    // Tension meter with the safe zone.
    const x = 318;
    const top = 26;
    const h = 186;
    g.fillStyle = 'rgba(0,0,0,0.45)';
    g.fillRect(x, top, 26, h);
    const [lo, hi] = this.zone();
    g.fillStyle = 'rgba(80,220,80,0.55)';
    g.fillRect(x, top + h * (1 - hi), 26, h * (hi - lo));
    g.fillStyle = this.tension > 0.88 ? '#ff4d3a' : '#ffd400';
    g.fillRect(x - 6, top + h * (1 - this.tension) - 3, 38, 6);
    g.fillStyle = '#fff';
    g.font = '12px Anton, Impact, sans-serif';
    g.textAlign = 'center';
    g.fillText('PULL', x + 13, top - 6);

    // How much fight the fish has left.
    g.fillStyle = 'rgba(0,0,0,0.45)';
    g.fillRect(16, 14, 200, 12);
    g.fillStyle = '#4fd1ff';
    g.fillRect(16, 14, 200 * clamp(this.fight, 0, 1), 12);
    g.fillStyle = '#fff';
    g.textAlign = 'left';
    g.fillText(`FISH FIGHT · ~${this.lbs} LB`, 18, 40);
  }

  private drawOver(g: CanvasRenderingContext2D): void {
    const o = this.outcome;
    if (!o) return;
    const text =
      o.kind === 'caught'
        ? `GOT 'EM! ${o.lbs} LB!`
        : o.kind === 'snapper'
          ? 'SNAPPING TURTLE!!'
          : o.kind === 'ripped'
            ? 'ARM SLIPPED OUT!'
            : o.kind === 'escaped'
              ? 'IT GOT AWAY'
              : o.kind === 'boot'
                ? 'A BOOT?'
                : 'JUST MUD';
    g.fillStyle = 'rgba(0,0,0,0.45)';
    g.fillRect(0, 88, this.width, 64);
    banner(g, text, this.width / 2, 132, o.kind === 'caught' ? '#ffd400' : '#ff8a6a', 36);
  }
}
